import { QUEST_STREAM_QUERY_POLICY } from 'api/queryPolicies'
import { getMemesQueryKey, MemeTopWindow, memeOrderParams } from 'lib/memeSort'
import { getQuestsQueryKey, serializeFilterKey } from 'refactor/hooks/api/useGetQuestsQueryKey'

export type StandardTopOrder = 'upvote' | 'upvalue'

// Time windows for the Top orders — mirrored by TopWindow on the backend
// (whitelist + created_at cutoffs). Absent window = all time.
export type TopWindowKey = '1h' | '24h' | 'week' | 'month' | 'year'

const TOP_WINDOW_LABELS: Record<TopWindowKey, string> = {
    '1h': 'Top 1h',
    '24h': 'Top 24h',
    week: 'Top Week',
    month: 'Top Month',
    year: 'Top Year',
}

// `1h` remains a supported filter value for saved filters and API compatibility,
// but is intentionally not offered in the Top dropdown.
export const TOP_WINDOW_OPTIONS: Array<{ window: TopWindowKey; label: string; tip: string }> = [
    { window: '24h', label: 'Top 24h', tip: 'Top threads started in the last 24 hours' },
    { window: 'week', label: 'Top Week', tip: 'Top threads started in the last week' },
    { window: 'month', label: 'Top Month', tip: 'Top threads started in the last month' },
    { window: 'year', label: 'Top Year', tip: 'Top threads started in the last year' },
]

const TOP_WINDOW_KEYS = Object.keys(TOP_WINDOW_LABELS) as TopWindowKey[]

export const isTopWindow = (window: unknown): window is TopWindowKey =>
    TOP_WINDOW_KEYS.includes(window as TopWindowKey)

// The window the ordinals media stream should be asked for: only the plain
// upvalue Top takes one (mirrors TopWindow.for on the backend). Top New tiers
// by seen state instead, and the other orders ignore windows entirely.
export function ordinalsTopWindowParam(
    order: unknown,
    topWindow: unknown,
): TopWindowKey | undefined {
    return order === 'upvalue' && isTopWindow(topWindow) ? topWindow : undefined
}

// Every order token the Top pill can represent. The standard orders rank by
// lifetime value (optionally windowed); top_new layers per-viewer seen-tiering
// on the same ranking and never takes a window.
export const TOP_ORDER_KEYS = ['top_new', 'upvote', 'upvalue'] as const

export const isTopOrder = (order: unknown): boolean =>
    TOP_ORDER_KEYS.includes(order as (typeof TOP_ORDER_KEYS)[number])

// Orders for the Market lens (listings only): New is the newest listing,
// Price is the lowest ask, and Top is the wall's Top pill unchanged, with
// its dropdown (Top New when enabled, then all-time Top). The wall's Hot
// (mint-age decay) is the one order left out: it ranks ordinals, not asks.
// `price_asc` is market-only on the backend and degrades to chronological
// anywhere else.
export const MARKET_ORDER_CONFIGS = {
    time: {
        icon: 'fa fa-clock-o',
        label: 'New',
        tip: 'Newest listings first',
    },
    price_asc: {
        icon: 'fa fa-tag',
        label: 'Price',
        tip: 'Lowest asking price first',
    },
    upvalue: {
        icon: 'fa fa-arrow-up fa-large',
        label: 'Top',
        tip: 'Most upvalued listings',
    },
}

export type StreamOrderConfig = { icon: string; label: string; tip: string }

export function getMarketOrderMapping(
    topNewEnabled: boolean,
): Record<string, StreamOrderConfig & { top?: TopOrderMeta }> {
    return {
        time: MARKET_ORDER_CONFIGS.time,
        price_asc: MARKET_ORDER_CONFIGS.price_asc,
        ...getTopOrderEntry(topNewEnabled, 'upvalue', MARKET_ORDER_CONFIGS.upvalue),
    }
}

type StreamOrderQueryState = {
    dataUpdatedAt: number
    isInvalidated: boolean
}

export const shouldInvalidateStreamOrderQuery = (
    state: StreamOrderQueryState | undefined,
    now = Date.now(),
): boolean =>
    !!state?.dataUpdatedAt &&
    !state.isInvalidated &&
    now - state.dataUpdatedAt >= QUEST_STREAM_QUERY_POLICY.staleTime

export const getStreamOrderQueryKey = (filter: Record<string, unknown>) => {
    if (!filter.search && (filter.ordinals || filter.user_ordinals)) {
        return ['ordinalsMediaStream', serializeFilterKey(filter)]
    }
    if (!filter.search && filter.memes) {
        const { sort, topWindow } = memeOrderParams(
            typeof filter.order === 'string' ? filter.order : undefined,
            isTopWindow(filter.top_window) ? (filter.top_window as MemeTopWindow) : undefined,
        )
        return getMemesQueryKey(
            sort,
            topWindow,
            true,
            filter.memes_scope === 'mine' ? 'mine' : undefined,
        )
    }
    return getQuestsQueryKey(filter)
}

// What the pill reads when the current selection is a Top order.
export function topOrderLabel(order: unknown, topWindow?: unknown): string {
    if (order === 'top_new') return 'Top New'
    if (isTopWindow(topWindow)) {
        return TOP_WINDOW_LABELS[topWindow]
    }
    return 'Top'
}

// Metadata the Top pill's dropdown needs: which standard order this surface
// ranks by, and whether the TOP NEW option is available to this viewer.
export type TopOrderMeta = {
    standardOrder: StandardTopOrder
    topNewEnabled: boolean
}

export function getDisplayedTopSelection(
    isSelected: boolean,
    effectiveOrder: string | undefined,
    effectiveWindow: TopWindowKey | undefined,
    meta: TopOrderMeta,
): { order: string; window: TopWindowKey | null } {
    const order = isSelected
        ? effectiveOrder ?? meta.standardOrder
        : meta.topNewEnabled
        ? 'top_new'
        : meta.standardOrder
    const window = isSelected && order !== 'top_new' && effectiveWindow ? effectiveWindow : null

    return { order, window }
}

export function getTopOrderEntry<T extends { label: string }>(
    topNewEnabled: boolean,
    standardOrder: StandardTopOrder,
    config: T,
): Record<string, T & { top: TopOrderMeta }> {
    const order = topNewEnabled ? 'top_new' : standardOrder
    const displayedConfig = topNewEnabled ? { ...config, label: 'Top New' } : config

    return { [order]: { ...displayedConfig, top: { standardOrder, topNewEnabled } } }
}
